import { Outlet } from 'react-router-dom'

import { BlankSlate, Box, Button, Hint, Link, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PayrollPolicyDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { formatTime } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { isCurrency } from 'modules/core/lib/money'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import app from 'modules/core/store/models/app'

import FadeInBox from 'modules/payroll_bundle/components/FadeInBox'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import PayrollPageLoading from 'modules/payroll_bundle/components/PayrollPageLoading'
import PayrollSummaryImportModal from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal'
import { ResultValidationsBanner } from 'modules/payroll_bundle/components/ResultValidationsBanner'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { useResultsPageLoader } from 'modules/payroll_bundle/hooks/useResultsPageLoader'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import {
  MAP_TO_CONCEPTS,
  RESET_DATA,
  REVIEW_AND_IMPORT,
  ResultValidationStatus,
  SELECT_DATA,
  UPDATE_DATA,
} from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { documentAuthor } from 'modules/payroll_bundle/lib/utils'
import resultsCollection from 'modules/payroll_bundle/store/collections/results'

import Results from './Results'
import ResultsFilesModal from './_components/ResultsFilesModal'

const SOURCE = 'payroll-results-page'

const ResultsPage = () => {
  const payrollBundleContext = usePayrollBundlePageContext()

  const { policyId, currentPeriod } = usePolicyPeriods()

  const { access, company, employee } = useCurrentSession()
  const {
    resultDispatch,
    payrollResultsStage,
    showUploadModal,
    showResultsFilesModal,
    selectedPayrollResultId,
    uploadedPayrollResults,
  } = usePayrollResults()
  const { data } = useQuery(PayrollPolicyDocument, {
    variables: {
      policyId: String(policyId),
    },
  })

  // There was a time when policies didn't have currency, and thus we need fallbacks
  const _currency =
    data?.payroll.policy?.currency || employee?.legalEntity?.currency || company.currency || 'EUR'

  const currency = isCurrency(_currency) ? _currency : 'EUR'

  const {
    fetching,
    hasResults,
    resultValidations,
    fetchData: fetchPayrollResultData,
  } = useResultsPageLoader()

  const toBeReviewedValidations = resultValidations.filter((item) => {
    return (
      item.status === ResultValidationStatus.warning ||
      item.status === ResultValidationStatus.invalid
    )
  })

  const resultsCollectionDocumentIds = resultsCollection.models.map(
    (res) => res.attributes.document_id
  )
  const pendingResults = uploadedPayrollResults.filter(
    (el) => !resultsCollectionDocumentIds.includes(el.id)
  )

  const selectedPayrollResult = uploadedPayrollResults.find(
    (result) => result.id === selectedPayrollResultId
  )

  const hasOngoingProcess = useMemo(() => {
    return [MAP_TO_CONCEPTS, SELECT_DATA, REVIEW_AND_IMPORT].includes(payrollResultsStage)
  }, [payrollResultsStage])

  const openResultFilesModal = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showResultsFilesModal: true,
      },
    })
  }

  const { loading, canSeeCompanyPayrollResults } = usePayrollContextlessPolicies()

  const hasPayrollResultsValidation = hasFeature('dev_payroll_results_validation')

  if (fetching || loading || !data) {
    return <PayrollPageLoading />
  }

  if (!canSeeCompanyPayrollResults) {
    return (
      <Box height="full">
        <BlankSlate
          title={i18n.t('payroll_bundle.results_payments.forbidden_screen.title')}
          description={i18n.t('payroll_bundle.results_payments.forbidden_screen.description')}
          illustration="visibility"
        />
      </Box>
    )
  }

  const lastPendingResult = pendingResults[pendingResults.length - 1]
  const lastPendingResultsDate = lastPendingResult
    ? moment(lastPendingResult.attributes.created_at)
    : null
  const pendingResultsBookkeepers = pendingResults.map((r) => {
    const u = documentAuthor(r)
    return u
      ? getFullDisplayName({
          firstName: u.attributes.first_name,
          lastName: u.attributes.last_name,
        })
      : undefined
  })
  const lastPendingResultsBookkeeperName =
    pendingResultsBookkeepers[pendingResultsBookkeepers.length - 1]
  const differentPendingResultsBookkeepers = new Set(pendingResultsBookkeepers).size

  const shouldShowImportModal = showUploadModal && !selectedPayrollResult
  const shouldShowPendingResultModal = showUploadModal && selectedPayrollResult

  function handleOpenSidePanel() {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showResultValidationsSidePanel: true,
      },
    })
  }

  const showResultFiles = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showResultsFilesModal: true,
      },
    })
  }

  const handleOnCloseWithPendingResult = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: false,
        selectedPayrollResultId: null,
        showResultsFilesModal: true,
      },
    })
    resultDispatch({
      type: RESET_DATA,
    })
  }

  const handleOnClose = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: false,
      },
    })
  }

  const handleOnFinish = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: false,
      },
    })

    fetchPayrollResultData()

    app.setFlash({
      type: 'correct',
      message: i18n.t('payroll_bundle.payroll_results.review_and_import.success_message'),
    })

    resultDispatch({
      type: RESET_DATA,
    })
  }

  return (
    <>
      {!hasOngoingProcess && (
        <FadeInBox>
          <Box width="full" flexGrow>
            {shouldShowImportModal && (
              <PayrollSummaryImportModal
                companyId={company.id}
                source={SOURCE}
                onFinish={handleOnFinish}
                onClose={handleOnClose}
                initialPolicyId={policyId || undefined}
                initialPolicyPeriodId={currentPeriod?.id}
              />
            )}
            {shouldShowPendingResultModal && (
              <PayrollSummaryImportModal
                companyId={company.id}
                source={SOURCE}
                onFinish={handleOnFinish}
                onClose={handleOnCloseWithPendingResult}
                initialPolicyId={policyId || undefined}
                initialPolicyPeriodId={currentPeriod?.id}
                initialPayrollResultDocumentId={selectedPayrollResult?.id}
                initialPayrollResultDocumentFilename={selectedPayrollResult?.filename}
              />
            )}
            {showResultsFilesModal && <ResultsFilesModal />}
            {pendingResults.length > 0 && hasResults && !hasOngoingProcess && (
              <Box
                background="champagne200"
                gap="s8"
                height="s64"
                alignItems="center"
                justifyContent="spaceBetween"
                flexDirection="row"
                paddingX="s64"
                border={{
                  top: { width: 's1', style: 'solid', color: 'champagne1000' },
                  bottom: { width: 's1', style: 'solid', color: 'champagne1000' },
                }}
              >
                <Text.Normal>
                  {i18n.tView(
                    'payroll_bundle.payroll_results.result_files.pending_result_files_action',
                    {
                      bold_text: (
                        <Text.Normal weight="bold">
                          {pendingResults.length > 1
                            ? i18n.t(
                                'payroll_bundle.payroll_results.result_files.pending_result_files_emphasis',
                                {
                                  number: pendingResults.length,
                                }
                              )
                            : lastPendingResult?.filename}
                        </Text.Normal>
                      ),
                      bookkeeperName: (
                        <Text.Normal>
                          {differentPendingResultsBookkeepers > 1
                            ? i18n.t(
                                'payroll_bundle.payroll_results.result_files.pending_result_files_multiple_bookeepers',
                                {
                                  bookkeeperName: lastPendingResultsBookkeeperName,
                                  extraBookeeperNumber: differentPendingResultsBookkeepers - 1,
                                }
                              )
                            : lastPendingResultsBookkeeperName}
                        </Text.Normal>
                      ),
                      uploadDate: (
                        <Text.Normal>
                          {lastPendingResultsDate ? lastPendingResultsDate.format('ll') : '-'}
                        </Text.Normal>
                      ),
                      uploadTime: (
                        <Text.Normal>
                          {lastPendingResultsDate
                            ? formatTime(access, lastPendingResultsDate.format('HH:mm'))
                            : '-'}
                        </Text.Normal>
                      ),
                    }
                  )}
                </Text.Normal>
                <Link.Touchable onClick={openResultFilesModal}>
                  {i18n.t('payroll_bundle.payroll_results.result_files.manage')}
                </Link.Touchable>
              </Box>
            )}
            {!hasOngoingProcess && hasResults && (
              <>
                {hasPayrollResultsValidation && toBeReviewedValidations.length > 0 && (
                  <Box>
                    <ResultValidationsBanner
                      count={toBeReviewedValidations.length}
                      viewIssues={handleOpenSidePanel}
                    />
                  </Box>
                )}
                <Box paddingTop="s24" gap="s8">
                  <Text.Header2>
                    {i18n.t('payroll_bundle.results_payments.results.title')}
                  </Text.Header2>
                  <Box flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="row" gap="s8" alignItems="center">
                      <Text.Normal>
                        {i18n.t('payroll_bundle.results_payments.results.description')}
                      </Text.Normal>
                      <HelpCenterLink name="payroll_results_1">
                        <Text.Normal weight="medium">
                          {i18n.t('payroll_bundle.learn_more')}
                        </Text.Normal>
                      </HelpCenterLink>
                    </Box>
                    <Button
                      onClick={showResultFiles}
                      type="secondary"
                      disabled={resultsCollection.isEmpty}
                    >
                      {i18n.t('payroll_bundle.payroll_results.results.manage_results_files')}
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            <Box paddingTop="s16">
              <Results currency={currency} />
            </Box>
          </Box>
        </FadeInBox>
      )}
      <Outlet context={payrollBundleContext} />
    </>
  )
}

const WrappedResultsPage = () => {
  const [hidePayrollResultSetUpDataImportHint, setHidePayrollResultSetUpDataImportHint] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.hidePayrollResultSetUpDataImportHint)

  const handleSetUpDataImportHintFinish = () => {
    setHidePayrollResultSetUpDataImportHint(true)
  }
  return (
    <Hint.Popover.Provider
      steps={2}
      isHidden={!!hidePayrollResultSetUpDataImportHint}
      onFinish={handleSetUpDataImportHintFinish}
      onClose={handleSetUpDataImportHintFinish}
    >
      <ResultsPage />
    </Hint.Popover.Provider>
  )
}

export default observer(WrappedResultsPage)
